import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import ClabLogoPng from './ClabLogoPng';
import { useAuth } from '../../context/AuthContext';
import NotificationCenter from '../NotificationCenter';

const HeaderBar = ({ userRole = 'admin', onSidebarToggle, userName = 'John Doe', sidebarCollapsed, setSidebarCollapsed }) => {
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user } = useAuth();

  // Use authenticated user name when available, fallback to prop
  const displayName = useMemo(() => {
    return user?.full_name || user?.fullName || user?.username || userName;
  }, [user, userName]);

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'admin': 'Master Admin',
      'clab': 'CLAB User',
      'clab_user': 'CLAB User',
      'master_admin': 'Master Admin',
      'recruiter': 'Recruiter',
      'employer': 'Employer'
    };
    return roleMap[role] || 'User';
  };

  const getLoginRouteForRole = (role) => {
    const normalizedRole = String(role ?? '').toLowerCase();
    if (normalizedRole === 'employer' || normalizedRole === '3') {
      return '/employer-login';
    }
    if (normalizedRole === 'recruiter' || normalizedRole === '2') {
      return '/recruiter-login';
    }
    // Default: Admin/CLAB go to main login
    return '/login-screen';
  };

  const handleLogout = async () => {
    try {
      setShowProfileDropdown(false);
      const { error } = await signOut();
      
      if (error) {
        console.error('Logout error:', error.message);
        // Still redirect to login even if there's an error
      }
      
      const redirectRoute = getLoginRouteForRole(userRole || user?.role);
      navigate(redirectRoute, { replace: true });
    } catch (err) {
      console.error('Logout failed:', err);
      const redirectRoute = getLoginRouteForRole(userRole || user?.role);
      navigate(redirectRoute, { replace: true });
    }
  };

  const handleProfileClick = () => {
    setShowProfileDropdown(false);
  };

  const getSettingsRouteForRole = (role) => {
    const r = String(role ?? '').toLowerCase();
    if (r === 'admin' || r === 'master_admin') return '/admin-settings';
    if (r === 'clab' || r === 'clab_user' || r === '1') return '/clab-settings';
    if (r === 'recruiter') return '/recruiter-settings';
    return null;
  };

  const handleSettingsClick = () => {
    const route = getSettingsRouteForRole(userRole || user?.role);
    setShowProfileDropdown(false);
    if (route) {
      navigate(route);
    } else {
      console.warn('No settings route for role:', userRole || user?.role);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-surface border-b border-secondary-200 h-16 z-1000">
      <div className="flex items-center justify-between h-full px-6">
        {/* Left Section - Logo and Sidebar Toggle */}
        <div className="flex items-center space-x-4">
          {/* Mobile Sidebar Toggle */}
          <button
            onClick={onSidebarToggle}
            className="lg:hidden p-2 rounded-md text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900 transition-colors duration-150"
            aria-label="Toggle sidebar"
          >
            <Icon name="Menu" size={20} />
          </button>
          
          {/* Desktop Sidebar Toggle */}
          <button
            onClick={setSidebarCollapsed ? () => setSidebarCollapsed(!sidebarCollapsed) : onSidebarToggle}
            className="hidden lg:block p-2 rounded-md text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900 transition-colors duration-150"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon name={sidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={20} />
          </button>
          
          <div className="flex items-center space-x-3">
            {/* CLAB Logo - Use authentic PNG logo */}
            <ClabLogoPng size="md" showSubtext={true} className="hidden sm:flex" />
            <ClabLogoPng size="sm" className="sm:hidden" />
          </div>
        </div>

        {/* Right Section - Notifications and Profile */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <NotificationCenter userRole={userRole} />

          {/* User Profile */}
          <div className="relative">
            <button
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              className="flex items-center space-x-3 p-2 rounded-md hover:bg-secondary-100 transition-colors duration-150"
              aria-label="User menu"
            >
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-text-primary">{displayName}</p>
                <p className="text-xs text-text-secondary">{getRoleDisplayName(userRole)}</p>
              </div>
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <Icon name="User" size={16} color="#1e40af" />
              </div>
              <Icon name="ChevronDown" size={16} className="text-secondary-600" />
            </button>

            {/* Profile Dropdown */}
            {showProfileDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-surface rounded-lg shadow-xl border border-secondary-200 z-1001 animation-fade-in">
                <div className="p-3 border-b border-secondary-200">
                  <p className="text-sm font-medium text-text-primary">{displayName}</p>
                  <p className="text-xs text-text-secondary">{getRoleDisplayName(userRole)}</p>
                </div>
                <div className="py-2">
                  <button
                    onClick={handleProfileClick}
                    className="flex items-center w-full px-4 py-2 text-sm text-text-primary hover:bg-secondary-50 transition-colors duration-150"
                  >
                    <Icon name="User" size={16} className="mr-3" />
                    Profile
                  </button>
                  <button
                    onClick={handleSettingsClick}
                    className="flex items-center w-full px-4 py-2 text-sm text-text-primary hover:bg-secondary-50 transition-colors duration-150"
                  >
                    <Icon name="Settings" size={16} className="mr-3" />
                    Settings
                  </button>
                </div>
                <div className="border-t border-secondary-200 py-2">
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-error hover:bg-error-50 transition-colors duration-150"
                  >
                    <Icon name="LogOut" size={16} className="mr-3" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Click outside handlers */}
      {showProfileDropdown && (
        <div
          className="fixed inset-0 z-999"
          onClick={() => {
            setShowProfileDropdown(false);
          }}
        />
      )}
    </header>
  );
};

export default HeaderBar;
